import React, { useMemo } from 'react';
import BrandingFooter from '../common/BrandingFooter';
import MatchWidgetShell from '../common/MatchWidgetShell';
import TeamBadge, { pickTeamLogo } from '../common/TeamBadge';
import {
  countByPosition,
  groupInjuriesByTeam,
  playerInitials,
  positionMeta,
} from './injuryUtils';

function MedicalIcon({ className = '' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M12 4v16M4 12h16" />
      <circle cx="12" cy="12" r="10" strokeWidth="1.5" opacity="0.35" />
    </svg>
  );
}

function InjuryStatChip({ label, value, tone = 'default' }) {
  return (
    <div className={`sw-injury-stat sw-injury-stat--${tone}`}>
      <span className="sw-injury-stat__value">{value}</span>
      <span className="sw-injury-stat__label">{label}</span>
    </div>
  );
}

function InjuryRow({ row }) {
  const pos = positionMeta(row.position);
  return (
    <li className="sw-injury-row">
      <span className="sw-injury-row__avatar" aria-hidden>
        {playerInitials(row.player)}
      </span>
      <div className="sw-injury-row__body">
        <span className="sw-injury-row__name">{row.player}</span>
        {row.appearances != null && (
          <span className="sw-injury-row__meta">{row.appearances} apps this season</span>
        )}
      </div>
      <span className={`sw-injury-pos sw-injury-pos--${pos.tone}`}>{pos.label}</span>
      <span className="sw-injury-row__status" title="Unavailable">
        <MedicalIcon className="w-3.5 h-3.5" />
      </span>
    </li>
  );
}

function TeamInjuryPanel({ team, players, teamLogo, teamLogoProxy }) {
  const logoRow = {
    team,
    teamLogo: teamLogo || players[0]?.teamLogo,
    teamLogoProxy: teamLogoProxy || players[0]?.teamLogoProxy,
  };
  const byPos = countByPosition(players);

  return (
    <section className="sw-injury-team-panel">
      <header className="sw-injury-team-panel__head">
        <TeamBadge name={team} logoUrl={pickTeamLogo(logoRow)} size={36} />
        <div className="sw-injury-team-panel__titles min-w-0 flex-1">
          <span className="sw-injury-team-panel__name">{team}</span>
          <span className="sw-injury-team-panel__sub">
            {players.length} player{players.length !== 1 ? 's' : ''} sidelined
          </span>
        </div>
        <span className="sw-injury-team-panel__count">{players.length}</span>
      </header>
      {(byPos.fwd + byPos.def + byPos.mid + byPos.gk > 0) && (
        <div className="sw-injury-team-panel__tags">
          {byPos.fwd > 0 && <span className="sw-injury-pos sw-injury-pos--fwd">{byPos.fwd} FWD</span>}
          {byPos.mid > 0 && <span className="sw-injury-pos sw-injury-pos--mid">{byPos.mid} MID</span>}
          {byPos.def > 0 && <span className="sw-injury-pos sw-injury-pos--def">{byPos.def} DEF</span>}
          {byPos.gk > 0 && <span className="sw-injury-pos sw-injury-pos--gk">{byPos.gk} GK</span>}
        </div>
      )}
      <ul className="sw-injury-team-panel__list">
        {players.map((r) => (
          <InjuryRow key={`${r.player}-${r.team}`} row={r} />
        ))}
      </ul>
    </section>
  );
}

function InjuriesHero({
  total,
  clubCount,
  selectedTeam,
  teamPlayers,
  posCounts,
  teamLogo,
  teamLogoProxy,
}) {
  if (selectedTeam && teamPlayers?.length) {
    const logoRow = {
      team: selectedTeam,
      teamLogo: teamLogo || teamPlayers[0]?.teamLogo,
      teamLogoProxy: teamLogoProxy || teamPlayers[0]?.teamLogoProxy,
    };
    return (
      <div className="sw-injury-hero sw-injury-hero--single">
        <div className="sw-injury-hero__glow" aria-hidden />
        <div className="sw-injury-hero__inner">
          <TeamBadge name={selectedTeam} logoUrl={pickTeamLogo(logoRow)} size={64} />
          <h2 className="sw-injury-hero__title">{selectedTeam}</h2>
          <p className="sw-injury-hero__lead">
            <span className="sw-injury-hero__count">{teamPlayers.length}</span>
            {teamPlayers.length === 1 ? ' player' : ' players'} currently unavailable
          </p>
          <span className="sw-injury-hero__badge">
            <MedicalIcon className="w-3.5 h-3.5" />
            Injury list
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="sw-injury-hero">
      <div className="sw-injury-hero__glow" aria-hidden />
      <div className="sw-injury-hero__inner sw-injury-hero__inner--overview">
        <div className="sw-injury-hero__icon-wrap">
          <MedicalIcon className="w-8 h-8" />
        </div>
        <div>
          <h2 className="sw-injury-hero__title">Squad casualties</h2>
          <p className="sw-injury-hero__lead">
            <span className="sw-injury-hero__count">{total}</span> injured across{' '}
            <span className="sw-injury-hero__count">{clubCount}</span> club
            {clubCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
      <div className="sw-injury-hero__stats">
        <InjuryStatChip label="Total" value={total} tone="alert" />
        <InjuryStatChip label="Clubs" value={clubCount} />
        {posCounts.fwd > 0 && <InjuryStatChip label="Forwards" value={posCounts.fwd} tone="fwd" />}
        {posCounts.def > 0 && <InjuryStatChip label="Defenders" value={posCounts.def} tone="def" />}
        {posCounts.mid > 0 && <InjuryStatChip label="Midfield" value={posCounts.mid} tone="mid" />}
        {posCounts.gk > 0 && <InjuryStatChip label="Keepers" value={posCounts.gk} tone="gk" />}
      </div>
    </div>
  );
}

export default function Injuries({
  showBranding = false,
  competitionLabel = 'Premier League',
  injuries = [],
  selectedTeam = null,
  teamLogo = null,
  teamLogoProxy = null,
}) {
  const filtered = useMemo(() => {
    if (!selectedTeam) return injuries;
    return injuries.filter((r) => r.team === selectedTeam);
  }, [injuries, selectedTeam]);

  const groups = useMemo(() => groupInjuriesByTeam(filtered), [filtered]);
  const posCounts = useMemo(() => countByPosition(filtered), [filtered]);
  const total = filtered.length;
  const clubCount = groups.length;

  const subtitle = selectedTeam
    ? 'Club injury report'
    : 'Grouped by club — pick a team above to focus';

  const headerBadge = total > 0 && (
    <span className="sw-injury-header-badge">
      <MedicalIcon className="w-3 h-3" />
      {total} out
    </span>
  );

  return (
    <div className="w-full" style={{ color: 'var(--sw-text)' }}>
      <MatchWidgetShell
        competitionLabel={competitionLabel}
        title="Injuries & suspensions"
        subtitle={subtitle}
        badge={headerBadge}
      >
        {total > 0 ? (
          <>
            <InjuriesHero
              total={total}
              clubCount={clubCount}
              selectedTeam={selectedTeam}
              teamPlayers={selectedTeam ? groups[0]?.players : null}
              posCounts={posCounts}
              teamLogo={teamLogo}
              teamLogoProxy={teamLogoProxy}
            />
            <div className="sw-injury-body">
              {selectedTeam ? (
                <section className="sw-injury-roster">
                  <ul className="sw-injury-team-panel__list">
                    {groups[0]?.players.map((r) => (
                      <InjuryRow key={`${r.player}-${r.team}`} row={r} />
                    ))}
                  </ul>
                </section>
              ) : (
                <div className="sw-injury-body__grid">
                  {groups.map(({ team, players }) => (
                    <TeamInjuryPanel
                      key={team}
                      team={team}
                      players={players}
                      teamLogo={teamLogo}
                      teamLogoProxy={teamLogoProxy}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="sw-injury-empty">
            <div className="sw-injury-empty__icon">
              <MedicalIcon className="w-10 h-10" />
            </div>
            <p className="sw-injury-empty__title">All clear</p>
            <p className="sw-injury-empty__text">
              {selectedTeam
                ? `No injured players listed for ${selectedTeam}.`
                : 'No injured players listed for this competition.'}
            </p>
          </div>
        )}
      </MatchWidgetShell>
      {showBranding && <BrandingFooter />}
    </div>
  );
}
